import * as React from "react";
import {PropsWithChildren, useEffect, useRef, useState} from "react";

type CardProps = {
  image?: string;
  image2x?: string;
  categoryName?: string;
  headline?: string;
  shortText?: string;
  authorName?: string;
  authorImage?: string;
  linkText?: string;
  linkUrl?: string;
  linkTitle?: string;
  lightBackground?: boolean;
};

const Card: React.FC<PropsWithChildren<CardProps>> = ({
  image,
  image2x,
  categoryName,
  headline,
  shortText,
  authorName,
  authorImage,
  linkText,
  linkUrl,
  linkTitle,
  lightBackground = false,
}) => {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const [paragraphLineClass, setParagraphLineClass] = useState("line-clamp-three");
  const validAuthorImage = !!authorImage && new RegExp("\\.(jpg|png)", "i").test(authorImage);
  const validAuthorName = !!authorName && !new RegExp("gast|auth?or", "i").test(authorName);
  const authorFirstName = authorName?.split(" ")[0];

  useEffect(() => {
    if (headlineRef.current && headlineRef.current.clientHeight) {
      const headlineHeight = headlineRef.current.clientHeight;
      if (headlineHeight > 40) setParagraphLineClass("line-clamp-two");
      if (headlineHeight > 70) setParagraphLineClass("line-clamp-one");
    }
  }, [headlineRef]);

  return (
    <div className="card column-8 column-s-4">
      <a className="image-link" href={linkUrl} title={linkTitle}>
        <div className="card__img">
          <img
            src={image}
            srcSet={image2x ? `${image ?? ""} 1x, ${image2x} 2x` : undefined}
            alt={headline}
            loading="lazy"
          />
          <div className={`swiper-lazy-preloader ${lightBackground ? "" : "swiper-lazy-preloader-white"}`}>
            {/* loading spinner container */}
          </div>
        </div>
        <div className={`card__content ${lightBackground ? "card__content--light" : "card__content--dark"}`}>
          <div className="card__content__text">
            {categoryName && <span className="card__content__subtitle line-clamp-one mb-1">{categoryName}</span>}
            <h3 className="line-clamp-three" ref={headlineRef}>
              {headline}
            </h3>
            {shortText && <p className={paragraphLineClass}>{shortText}</p>}
          </div>
          <div className="card__footer">
            {(authorImage || authorName) && (
              <div className="card__footer__content">
                {authorImage && validAuthorImage && (
                  <img src={authorImage} alt={authorName} loading="lazy" className="mr-3" />
                )}
                {authorName && validAuthorName && <span>{authorFirstName}</span>}
              </div>
            )}
            {linkText && (
              <span className="link link-icon">
                {linkText}
                <i className={`icon ${lightBackground ? "icon-right--blue" : "icon-right--white"} ml-3`}></i>
              </span>
            )}
          </div>
        </div>
      </a>
    </div>
  );
};

export default Card;
